import React, { useState, useEffect } from 'react';
import { Save, Shield, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { getSecuritySettings, updateSecuritySettings } from '@/services/settingsApi';

export const SecuritySettingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [requireEmailVerification, setRequireEmailVerification] = useState(true);
  const [enableRateLimiting, setEnableRateLimiting] = useState(true);
  const [enableCommunityVoting, setEnableCommunityVoting] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await getSecuritySettings();
        const data = res?.data || res || {};
        setRequireEmailVerification(data.require_email_verification ?? true);
        setEnableRateLimiting(data.enable_rate_limiting ?? true);
        setEnableCommunityVoting(data.enable_community_voting ?? true);
      } catch (error) {
        console.error(error);
        toast.error('Failed to load security settings');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSecuritySettings({
        require_email_verification: requireEmailVerification,
        enable_rate_limiting: enableRateLimiting,
        enable_community_voting: enableCommunityVoting,
      });
      toast.success('Security settings saved');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-xl border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Security Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label>Require Email Verification</Label>
            <p className="text-xs text-slate-500">Users must verify email before accessing site</p>
          </div>
          <Switch
            checked={requireEmailVerification}
            onCheckedChange={setRequireEmailVerification}
          />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <Label>Enable Rate Limiting</Label>
            <p className="text-xs text-slate-500">Prevent abuse by limiting requests</p>
          </div>
          <Switch checked={enableRateLimiting} onCheckedChange={setEnableRateLimiting} />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <Label>Allow Community Voting</Label>
            <p className="text-xs text-slate-500">Enable voting on unlisted firms</p>
          </div>
          <Switch
            checked={enableCommunityVoting}
            onCheckedChange={setEnableCommunityVoting}
          />
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Changes
        </Button>
      </CardContent>
    </Card>
  );
};